import { createModal } from './modal.js';

/**
 * createMultiStepModal
 *
 * @param {Object<string, (ctx) => HTMLElement>} steps
 *   Map of stepName -> render function. Each render fn gets `ctx` and
 *   must return an HTMLElement (called fresh on every step change).
 * @param {Object} options
 * @param {string}            options.initialStep
 * @param {string|() => string} [options.title]
 * @param {boolean}           [options.closable=true]
 * @param {string}            [options.size='md']
 * @param {Function}          [options.onOpen]
 * @param {Function}          [options.onClose]
 */
export function createMultiStepModal(steps, options = {}) {
  let currentStep = options.initialStep;
  let stepData = {};

  const ctx = {
    // move to another step, optionally merging new shared data
    goTo(step, data) {
      currentStep = step;
      if (data) Object.assign(stepData, data);
      modalApi.setContent(renderCurrentStep); // same ref -> forces re-render
    },
    get data() {
      return stepData;
    },
    close: () => modalApi.close(),
  };

  function renderCurrentStep() {
    const builder = steps[currentStep];
    if (!builder) {
      throw new Error(`createMultiStepModal: unknown step "${currentStep}"`);
    }
    return builder(ctx);
  }

  const modalApi = createModal({
    title: () => (typeof options.title === 'function' ? options.title(ctx) : options.title),
    closable: options.closable ?? true,
    size: options.size ?? 'md',
    content: renderCurrentStep,
    onClose: options.onClose,
  });

  // wrap open() so every fresh open resets to the initial step
  function open(initialStep = options.initialStep, initialData = {}) {
    currentStep = initialStep;
    stepData = { ...initialData };
    modalApi.open();
    options.onOpen?.(ctx);
  }

  return { ...modalApi, open, goTo: ctx.goTo };
}
